import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Select } from 'tea-component';
import HeatMapTable, {
  SortItem,
  HeatCellDataStruct,
  creatHeatColumns,
  HeatMapTableColumn
} from '@/pages/dashboard/components/HeatMapTable';
import Accumulate from '@/pages/dashboard/components/Accumulate';
import MetricAndUpDown from '@/pages/dashboard/components/MetricAndUpDown';
import { commonValueFormatter, formatFilterValues, formatNum, formatPercentNum, getUpAndDownStatus, getWeekDay } from '@/pages/dashboard/utils/utils';
import { getLocale, useIntl, useSelector, shallowEqual } from 'umi';
import { DATE_FORMAT, KEY_DAYS_DEFAULT_VALUE } from '../../_config/constant';
import { useSelectArrObj, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { LANGUAGE_MAP } from '@/pages/dashboard/_config/constant';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import useData from './useData';
import { downLoadApi } from '@/pages/dashboard/services/common';
import { Props, TableReqParams } from './_type';
import { dynamicString } from '@home/utils/dynamicString';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';
import { resolveAndSelectName, prepareMetricsDataSource } from '@/pages/dashboard/utils/metricUtils';
import { ComponentMetricsItemType } from '@/pages/dashboard/components/PageContainer/type';
import './index.scss';
import { WIDTH_MAP_RETURN_TABLE } from '../RetentionReturnTableCo/_constant';

/**
 * 根据 tableMetric 配置筛选并排序指标列表
 * @param metrics 原始指标配置数组
 * @param tableMetric 需要筛选的指标 key 数组（决定顺序）
 * @returns 按 tableMetric 顺序排列的指标数组
 */
export const filterComponentMetricsByTableMetric = (
  metrics: ComponentMetricsItemType[],
  tableMetric?: string[]
): ComponentMetricsItemType[] => {
  // 未配置 tableMetric 时,返回全部指标
  if (!tableMetric || tableMetric?.length === 0) {
    return metrics;
  }

  // 创建 metricKey 到指标配置的映射
  const metricsMap = new Map<string, ComponentMetricsItemType>();
  metrics.forEach((metric) => {
    const metricKey = metric.reqMetricKey || metric.metricKey;
    metricsMap.set(metricKey, metric);
  });

  // 按照 tableMetric 的顺序返回指标
  return tableMetric
    ?.map((key) => metricsMap?.get(key))
    ?.filter((metric): metric is ComponentMetricsItemType => metric !== undefined);
};

export default function RetentionTable(props: Props) {
  const { config, data, style, className, tableMetric, title: propsTitle, titleCN: propsTitleCN, keyDaysConfig: propsKeyDaysConfig, api: propsApi,tableDisWidthMap: propsTableDisWidthMap, accumulateSelectMultipleBoxClassName } = props;
  const { title: configTitle, titleCN: configTitleCN, componentMetrics: configComponentMetrics, keyDaysConfig: configKeyDaysConfig, api: { tableDownloadApi: configTableDownloadApi }, tableDisWidthMap: configTableDisWidthMap } = config;
  const {game_code, data_frame} = useSelector((state: any) => state?.dashboard?.currentGame || {}, shallowEqual);
  // retentionColumsNum = 2 是mobile框架的默认值
  const { tag ,retentionColumsNum = '2', metricsInfo} = data || {};

  const { title, titleCN, componentMetrics, keyDaysConfig, api, tableDownloadApi, tableDisWidthMap } = useMemo(() => {
    return {
      title: propsTitle || configTitle,
      titleCN: propsTitleCN || configTitleCN,
      componentMetrics: filterComponentMetricsByTableMetric(configComponentMetrics || [], tableMetric),
      keyDaysConfig: propsKeyDaysConfig || configKeyDaysConfig,
      api: propsApi || config?.api,
      tableDownloadApi: propsApi?.tableDownloadApi || configTableDownloadApi,
      tableDisWidthMap: propsTableDisWidthMap || configTableDisWidthMap || WIDTH_MAP_RETURN_TABLE,
    };
  }, [propsTitle, configTitle, propsTitleCN, configTitleCN, configComponentMetrics, tableMetric, propsKeyDaysConfig, configKeyDaysConfig, propsApi, config?.api, propsTableDisWidthMap, configTableDisWidthMap]);

  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const isEn = getLocale() === 'en-US';
  const { formatMessage } = useIntl();

  // 准备 metricsData 用于 dynamicString 解析
  const metricsData = useMemo(() => {
    return prepareMetricsDataSource(metricsInfo);
  }, [metricsInfo]);
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: any) => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: true,
  });
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  // Key Days 筛选器
  const [keyDays, setKeyDays] = useState<number>(KEY_DAYS_DEFAULT_VALUE);
  // 宽表数据
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableCopyData, setTableCopyData] = useState<any[]>([]);
  // const [noTableData, setNoTableData] = useState<any[]>([])
  // 排序值
  const [sortValue, setSortValue] = useState<SortItem[]>([]);
  const [columns, setColumns] = useState<HeatMapTableColumn[]>([{ name: "", features: { sortable: true } }]); // 默认值是为了解决控制台warning, 无功能作用
  const [headKey, setHeadKay] = useState();
  // 表格分页记录
  const [tablePaging, setTablePaging] = useState({ pageIndex: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);
  const weekDayArr = useWeekDayArr();
  const accumulateOldValueRef = useRef(['date']);
  const retentionTableData = useData({ api }, filterVal, filterLoading, tag || 'new', accumulated || ['date']);
  const {
    getRetentionDataFunc,
    componentData,
    error,
    loading,
    overLimitBarTriggerKey,
  } = retentionTableData;
  const tableTitle = useMemo(() => isEn ? title : titleCN || '', [isEn, title, titleCN]);
  const { setNoticeStatus } = useNotificationOfDownloadTable(tableTitle || '');

  useEffect(() => {
    setTablePaging({ pageIndex: 1, pageSize: 10 });
    setKeyDays(KEY_DAYS_DEFAULT_VALUE);
    setSortValue([]);
  }, [filterVal]);

  const getHeatColumns = (keys: string[]) => creatHeatColumns(keys?.map((key: string) => {
    const type = key.slice(0, 1);
    let num = key.slice(1);
    // pc游戏除夜莺
    const isPcNotNight = data_frame === 'pc' && !game_code?.includes('nightingale');
    if (num === retentionColumsNum && !isEn && !isPcNotNight) {
      num = '次';
    }

    // 支持动态字符串获取 title
    // 优先从 metricsInfo 中获取配置（支持模板字符串）
    const metricInfo = metricsInfo?.[key];

    let title = '';
    if (metricInfo) {
      // 从 metricsInfo 中获取 en 和 cn，支持动态字符串解析
      const enName = metricInfo?.en;
      const cnName = metricInfo?.cn;
      title = resolveAndSelectName(isEn, enName, cnName, metricsInfo) || '';
    }

    // 如果没有配置 or 解析失败，使用原有的 formatMessage 逻辑（兼容性）
    if (!title) {
      title = formatMessage({ id: `engagement_retention_${type?.toLowerCase() ?? 'd'}${isPcNotNight?'_new':''}` }, { num });
    }

    return ({
      code: `__heat_${key}`,
      title
    }) as any;
  }));

  const getNormalColumns = (): HeatMapTableColumn[] => {
    // register列配置
    const metricsConfig = componentMetrics?.[0];
    const { enName, cnName, type, decimal, percentDecimals, formatRule, dodFormatRule } = metricsConfig || {};
    // 支持 dynamicString 解析列名
    const name = resolveAndSelectName(isEn, enName, cnName, metricsInfo) || (isEn ? enName : cnName) || '';
    const REGISTER_COLUMN: HeatMapTableColumn = {
      code: 'register_cnt',
      // width: 180,
      width: 200,
      name, // todo 是否可以用一个
      title: name, // todo 是否可以用一个
      align: 'right',
      features: { sortable: true },
      render: (record) => {
        const { count = '', dod = '', status } = record || {};
        // status = status ? status : Number(dod) < 0 ? 'down' : 'up'
        status = getUpAndDownStatus(dod, !!status);

        // 支持 formatRule 格式化主值
        let metricValue: string | number;
        if (formatRule) {
          // 使用 formatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedFormatRule: string = dynamicString(formatRule as string, metricsData);
          const formattedValue = dataCommonFormat({ value: count, formatRule: resolvedFormatRule });
          metricValue = (formattedValue ?? commonValueFormatter(type || 'int', count, decimal || 0)) || '';
        } else {
          // 兼容旧逻辑：使用 type + decimal
          metricValue = commonValueFormatter(type || 'int', count, decimal || 0) || '';
        }

        // 支持 dodFormatRule 格式化环比值
        let dodData: string | number;
        if (dodFormatRule) {
          // 使用 dodFormatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedDodFormatRule: string = dynamicString(dodFormatRule as string, metricsData);
          const formattedDod = dataCommonFormat({ value: dod, formatRule: resolvedDodFormatRule });
          dodData = (formattedDod ?? commonValueFormatter('percent', dod, percentDecimals || 1)) || '';
        } else {
          // 兼容旧逻辑：使用 percentDecimals
          dodData = commonValueFormatter('percent', dod, percentDecimals || 1) || '';
        }

        return (
          <MetricAndUpDown
            metric={metricValue}
            updown={{ data: dodData, status }}
            hasTooltip={false}
          />
        );
      },
    };
    const selectHeader: any = {
      "zone": formatMessage({ id: 'common_Zone' }),
      "region": formatMessage({ id: 'common_Region' }),
      "country": formatMessage({ id: 'common_Country' }),
      "system": formatMessage({ id: 'common_System' }),
      "channel": formatMessage({ id: 'common_Channel' }),
      "language": formatMessage({ id: 'common_Language' }),
      "bundle": formatMessage({ id: 'common_Bundle' }),
      "ua_network": formatMessage({ id: 'common_ua_network' }),
      "version": formatMessage({ id: 'common_version' }),
      "campaign": formatMessage({ id: 'common_campaign' }),
    };

    // accumulate列配置
    const accumulateColumns: HeatMapTableColumn[] = [];
    accumulated.map((val, i) => {
      if (i > 0) {
        const option = SEPARATED_OPTIONS.find((opt: { value: string; }) => opt.value === val);
        option && accumulateColumns.push({
          code: val,
          width: tableDisWidthMap?.[val as keyof typeof tableDisWidthMap] || 170,
          align: 'left',
          name: selectHeader[option?.value],
          title: selectHeader[option?.value],
          features: { sortable: true },
        });
      }
    });
    const DATE_COLUMN: HeatMapTableColumn = {
      code: 'date',
      width: 160,
      align: 'left' as const,
      lock: true,
      features: { sortable: true },
      name: formatMessage({ id: 'engagement_retention_date' }),
      render: (record: any) => {
        const weekDay = getWeekDay(record, weekDayArr)?.slice(1, -1);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return (filterVal?.timeType === "Month" ? (moment(record).isValid() ? moment(record).format("YYYY-MM") : record) : (
          <>
            <span className='table-text'>{moment(record).isValid() ? moment(record).format("YYYY-MM-DD") : record}</span>
            {moment(record).isValid() && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>
        ));
      }
    };
    // 正常来说metricsConfig是必定存在的
    return (metricsConfig ? [DATE_COLUMN, ...accumulateColumns, REGISTER_COLUMN] : [DATE_COLUMN, ...accumulateColumns]);
  };

  const heatCellColorPercent = (percentageStr: string) => {
    const floatNum = parseFloat(percentageStr);
    if (floatNum >= 0 && floatNum <= 0.1) return 0.05;
    if (floatNum > 0.1 && floatNum <= 0.2) return 0.1;
    if (floatNum > 0.2 && floatNum <= 0.3) return 0.2;
    if (floatNum > 0.3 && floatNum <= 0.4) return 0.3;
    if (floatNum > 0.4 && floatNum <= 0.5) return 0.4;
    if (floatNum > 0.5 && floatNum <= 0.6) return 0.5;
    if (floatNum > 0.6 && floatNum <= 0.7) return 0.6;
    if (floatNum > 0.7 && floatNum <= 0.8) return 0.7;
    if (floatNum > 0.8 && floatNum <= 0.9) return 0.8;
    return 0.85;
  };

  const {
    averageName,
    retentionUsersName,
    retentionRateName,
    retentionUsersFormatter,
    retentionRateFormatter,
  } = useMemo(() => {
    const usersFormatter = metricsInfo?.retention_users_count?.formatter;
    const rateFormatter = metricsInfo?.retention_rate?.formatter;

    const resolvedUsersFormatter = usersFormatter ? dynamicString(usersFormatter, metricsData, '') : '';
    const resolvedRateFormatter = rateFormatter ? dynamicString(rateFormatter, metricsData, '') : '';

    return {
      averageName:
        resolveAndSelectName(
          isEn,
          dynamicString(`{{metricsInfo.average_retention_users.en}}`, metricsData, ''),
          dynamicString(`{{metricsInfo.average_retention_users.cn}}`, metricsData, ''),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_average' }),
      retentionUsersName:
        resolveAndSelectName(
          isEn,
          dynamicString(`{{metricsInfo.retention_users_count.en}}`, metricsData, ''),
          dynamicString(`{{metricsInfo.retention_users_count.cn}}`, metricsData, ''),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_user' }),
      retentionRateName:
        resolveAndSelectName(
          isEn,
          dynamicString(`{{metricsInfo.retention_rate.en}}`, metricsData, ''),
          dynamicString(`{{metricsInfo.retention_rate.cn}}`, metricsData, ''),
          metricsInfo,
        ) || formatMessage({ id: 'engagement_retention_rate' }),
      retentionUsersFormatter: resolvedUsersFormatter,
      retentionRateFormatter: resolvedRateFormatter,
    };
  }, [isEn, metricsData, metricsInfo, formatMessage]);

  /** 返回数据格式整理 */
  const handleTableData = (data: any[] = [], isCopy?: boolean) => data.map(({ type, date: dateValue, count, dod, group_by_cn = [], group_by_en = [], rentention_detail = [], status } = {}) => {
    const timeType = (filterVal?.timeType || 'Day') as keyof typeof DATE_FORMAT;
    const date = moment(dateValue).isValid() ? moment(dateValue).format(DATE_FORMAT[timeType]) : dateValue;

    // 计算 D/W/M2 ~ D/W/M90数据
    const details: HeatCellDataStruct = {};
    rentention_detail.forEach((detail: string) => {
      const [key, percentageStr, userCount = '-'] = detail.split(':');
      // 留存率：有 formatter 时优先使用 formatter，否则走旧逻辑
      const rateValue = percentageStr === '' ? '-' : percentageStr;
      const cellContent = isCopy
        ? rateValue
        : retentionRateFormatter
          ? dataCommonFormat({ value: Number(rateValue), formatRule: retentionRateFormatter }) || formatPercentNum(rateValue || '0')
          : formatPercentNum(rateValue || '0');

      if (key) {
        const numPercentage = Number(percentageStr || '0');
        (details as any)[`__heat_${key}`] = {
          bubble: {
            content: (<>
              <span className='table-bubble-date'>{date === 'Average' ? averageName : date}</span>
              <div className='table-bubble-info'>
                <span className="Revenue-table-Lspan">{retentionUsersName}</span>
                <span className='Revenue-table-Rspan'>{
                  userCount === ''
                    ? '-'
                    : (retentionUsersFormatter
                      ? dataCommonFormat({ value: Number(userCount || 0), formatRule: retentionUsersFormatter }) || formatNum(Number(userCount || 0))
                      : formatNum(Number(userCount || 0)))
                }</span>
              </div>
              <div className='table-bubble-info'>
                <span className="Revenue-table-Lspan">{retentionRateName}</span>
                <span className='Revenue-table-Rspan'>{String(cellContent)}</span>
              </div>
            </>),
            placement: 'left',
          },
          cellContent: String(cellContent),
          colorPercent: heatCellColorPercent(isNaN(numPercentage) ? 0 : numPercentage),
        };
        if (type === 'avg' && (details as any)[`__heat_${key}`]) {
          (details as any)[`__heat_${key}`].bubble.visible = false;
        }
      }
    });

    // 计算 Accumulate筛选器的动态列的数据
    const accumulatedData: any = {};
    accumulated.map((value, index) => {
      if (index > 0) {
        if (!isEn) {
          accumulatedData[value] = group_by_cn?.[index - 1] || '-';
        } else {
          accumulatedData[value] = group_by_en?.[index - 1] || '-';
        }
      }
    });

    return {
      date,
      ...accumulatedData,
      register_cnt: {
        dod,
        count: isCopy ? count : count === '' ? '-' : count,
        status,
      },
      ...details,
    };
  });

  useEffect(() => {
    const rententionDays = componentData?.rentention_days as any;
    const keys = rententionDays?.[0]?.rentention_detail?.map((detail: string) => detail.split(':')[0]);
    // 表格数据最后一页的最后一行加入 avg 行
    const data = (() => {
      let data: any = componentData?.rentention_days;
      if (componentData?.avg?.length > 0 && componentData?.rentention_days?.length > 0) {
        data = [
          ...(data || []),
          {
            date: formatMessage({ id: 'engagement_retention_average' }),
            count: '',
            dod: '',
            status: 'no',
            group_by: [],
            rentention_detail: componentData?.avg,
            type: 'avg'
          },
        ];
      }
      return data;
    })();
    setHeadKay(keys);
    setColumns([...getNormalColumns(), ...getHeatColumns(keys)]);
    setTableData(handleTableData(data, false));
    setTableCopyData(handleTableData(data, true));
    // setNoTableData(data)
    setTotal(componentData?.total_record || 0);
  }, [componentData, isEn]);

  const KEY_DAYS_OPTIONS = useMemo(() => keyDaysConfig?.map((item: { value: any; enName: any; cnName: any; }) => ({
    value: item?.value,
    text: isEn ? item?.enName : item?.cnName
  })), [isEn, keyDaysConfig]);

  useEffect(() => {
    if (headKey) {
      setColumns([...getNormalColumns(), ...getHeatColumns(headKey)]);
    }
    // if (tableData.length) {
    //   noTableData.forEach(item => {
    //     if (['Average', '加权留存值'].includes(item.date)) item.date = formatMessage({ id: 'engagement_retention_average' })
    //   })
    //   setTableData(handleTableData(noTableData))
    // }
  }, [isEn]);
  /**
   * 获取上传参数对象
   */
  const getTableReqParams = (paging: { pageIndex: any; pageSize: any; }, sort: any[]): TableReqParams => {
    const { rangeTime, diff, timeType, ...filters } = filterVal || {};
    const cycle = timeType?.replace(timeType[0], timeType[0]?.toLowerCase());

    let desc = true;
    const orderBy = sort.map((value) => {
      desc = value.order === 'desc';
      return value.code?.replace('__heat_', '');
    });

    return {
      game_code: game_code || '',
      start_time: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      end_time: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      tag: tag || 'new',
      filters: formatFilterValues(filters),
      cycle: (cycle || 'day') as string,
      column_len: keyDays,
      group: accumulated?.slice(1) || [], // 0的位置一直是'date'，删除
      desc,
      order_by: orderBy.length ? orderBy : ['date'],
      page: paging.pageIndex,
      page_size: paging.pageSize,
    };
  };

  /**
   * 下载文件
   */
  const download = () => {
    const downloadPromise = downPromise?.();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${tableTitle} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  };

  const downPromise = () => {
    const params: TableReqParams = getTableReqParams(tablePaging, sortValue);
    const language = LANGUAGE_MAP[getLocale()];
    return downLoadApi?.(`${tableDownloadApi}?language=${language}`, params);
  };

  return (
    <div className={`dbd-retention-table-co ${className}`} style={style}>
      <HeatMapTable
        title={tableTitle}
        timeType={filterVal?.timeType}
        records={tableData}
        copyData={tableCopyData}
        defaultColumnWidth={50}
        columns={columns}
        sorting={{
          sorts: sortValue,
          onChangeSorts: (nextSorts) => {
            setSortValue(nextSorts);
            getRetentionDataFunc(keyDays, tablePaging, nextSorts);
          },
        }}
        paging={{
          recordCount: total,
          pageSize: tablePaging.pageSize,
          pageIndex: tablePaging.pageIndex,
          onPagingChange: (pagingQuery) => {
            setTablePaging(pagingQuery);
            getRetentionDataFunc(keyDays, pagingQuery, sortValue);
          },
          pageSizeOptions: [10, 20, 30, 50],
        }}
        loading={loading}
        error={error}
        onDownload={download}
        frontLogicalFlag={componentData?.front_logical_flag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
        topTip={
          {
            left: (<>
              <Accumulate
                accumulateSelectMultipleBoxClassName={accumulateSelectMultipleBoxClassName}
                className='register-table-select'
                value={accumulated}
                selectOptions={SEPARATED_OPTIONS}
                onChange={(value) => {
                  setAccumulated(value);
                }}
                onClose={() => {
                  if (accumulateOldValueRef?.current?.toString() === accumulated?.toString()) {
                    return;
                  }
                  accumulateOldValueRef.current = accumulated;
                  // 分页和排序重置
                  setTablePaging({ pageIndex: 1, pageSize: 10 });
                  setSortValue([]);
                  getRetentionDataFunc(keyDays, { pageIndex: 1, pageSize: 10 }, []);

                }}
              />

              {filterVal?.timeType === 'Day' ? (
                <Select
                  className='register-table-select'
                  boxClassName='register-table-select-box'
                  appearance="button"
                  matchButtonWidth
                  // style={{ width: 150 }}
                  options={KEY_DAYS_OPTIONS}
                  value={keyDays}
                  onChange={(value) => {
                    setKeyDays(value);
                    getRetentionDataFunc(value, tablePaging, sortValue);
                  }}
                  placeholder={formatMessage({ id: 'engagement_retention_keydays' })}
                />
              ) : (
                ''
              )}
            </>)
          }
        }
      />
    </div>

  );
}
